import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { APP_ENTRY_ROUTE, HERO_HEADLINES, HERO_HEADLINE_INTERVAL_MS, HERO_STATS } from "../landingContent";

export function HeroSection() {
  const activeHeadline = useRotatingHeadline();

  return (
    <section id="top" className="relative overflow-hidden border-b" style={{ borderColor: "var(--line)" }}>
      {/* Three stacked decorative layers, none of them interactive. */}
      <div className="pointer-events-none absolute inset-0" style={{ background: "var(--hero-wash)" }} />
      <div
        className="pointer-events-none absolute left-1/2 h-[520px] w-[900px] -translate-x-1/2"
        style={{
          top: -180,
          background: "radial-gradient(ellipse at center, var(--glow-a) 0%, transparent 68%)",
          filter: "blur(8px)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          opacity: "var(--grid-opacity)",
          backgroundImage:
            "linear-gradient(to right, var(--grid-line) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, #000, transparent)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, #000, transparent)",
        }}
      />

      <div className="l-container relative flex flex-col items-center pb-72 pt-96 text-center">
        <div
          data-reveal
          className="mb-26 inline-flex items-center gap-9 rounded-full border py-6 pl-8 pr-14"
          style={{ borderColor: "var(--line-strong)", background: "var(--surface-muted)" }}
        >
          <span className="l-live-chip">
            <span className="l-dot" />
            Live
          </span>
          <span
            className="l-mono text-[12.5px] font-semibold uppercase"
            style={{ letterSpacing: "0.07em", color: "var(--ink-muted)" }}
          >
            The decentralized perpetuals exchange
          </span>
        </div>

        {/* Every headline occupies the same grid cell, so the hero reserves the
            height of the tallest one and nothing below it shifts on a swap. */}
        <h1 data-reveal className="l-h1 l-headline-stack">
          {HERO_HEADLINES.map((headline, index) => {
            const isActive = index === activeHeadline;

            return (
              <span
                key={headline.accent}
                className="l-headline"
                data-active={isActive ? "" : undefined}
                aria-hidden={isActive ? undefined : true}
              >
                {headline.lead}
                <br />
                <span className="l-headline-accent">{headline.accent}</span>
              </span>
            );
          })}
        </h1>

        <p
          data-reveal
          className="mt-24"
          style={{
            fontSize: "clamp(16px, 2.1vw, 19px)",
            lineHeight: 1.55,
            color: "var(--ink-muted)",
            maxWidth: "62ch",
            textWrap: "pretty",
          }}
        >
          Zero-slippage execution on perpetual futures. Orders clear against aggregated depth at the quoted price — no
          spread tax, no adverse fills, settled on-chain in under a second.
        </p>

        <div data-reveal className="mt-34 flex flex-wrap justify-center gap-12">
          <Link
            to={APP_ENTRY_ROUTE}
            className="l-pill l-pill-primary h-50 px-30 text-16"
            style={{ boxShadow: "0 10px 34px var(--accent-glow)" }}
          >
            Launch App <span className="opacity-75">»</span>
          </Link>
          <a href="#how" className="l-pill l-pill-ghost h-50 px-28 text-16">
            Read the Docs
          </a>
        </div>

        <div
          data-reveal
          className="l-hairline mt-64 w-full rounded-18"
          style={{ maxWidth: 960, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}
        >
          {HERO_STATS.map((stat) => (
            <div key={stat.label} className="flex flex-col items-center gap-6 px-20 py-22">
              <span className="l-micro">{stat.label}</span>
              <span
                className="l-nums"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "clamp(24px, 3.4vw, 31px)",
                  fontWeight: 700,
                  letterSpacing: "-0.028em",
                  lineHeight: 1.05,
                }}
              >
                {stat.value}
              </span>
              <span className="text-12" style={{ color: "var(--ink-subtle)" }}>
                {stat.meta}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Index of the hero headline currently on screen.
 *
 * Paused while the tab is hidden so a visitor coming back does not land
 * mid-crossfade, and left on the first headline when the visitor has asked for
 * reduced motion — auto-rotating copy is the thing that setting is about.
 */
function useRotatingHeadline() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (HERO_HEADLINES.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    let timer = 0;

    const start = () => {
      timer = window.setInterval(() => {
        setIndex((current) => (current + 1) % HERO_HEADLINES.length);
      }, HERO_HEADLINE_INTERVAL_MS);
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = 0;
    };

    const onVisibilityChange = () => (document.hidden ? stop() : start());

    start();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return index;
}
